import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { api, formatPrice } from "@/lib/api";
import { toast } from "sonner";
import { Lock, Gift, Truck, Zap, LogIn, FlaskConical } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

const FIELDS = [
  ["first_name", "First Name", "text"],
  ["last_name", "Last Name", "text"],
  ["email", "Email", "email"],
  ["phone", "Phone", "tel"],
  ["address1", "Address", "text"],
  ["address2", "Apt / Suite (optional)", "text"],
  ["city", "City", "text"],
  ["region", "State", "select"],
  ["zip", "ZIP / Postal Code", "text"],
];

const US_STATES = [
  ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"], ["CA", "California"],
  ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"], ["DC", "District of Columbia"],
  ["FL", "Florida"], ["GA", "Georgia"], ["HI", "Hawaii"], ["ID", "Idaho"], ["IL", "Illinois"],
  ["IN", "Indiana"], ["IA", "Iowa"], ["KS", "Kansas"], ["KY", "Kentucky"], ["LA", "Louisiana"],
  ["ME", "Maine"], ["MD", "Maryland"], ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"],
  ["MS", "Mississippi"], ["MO", "Missouri"], ["MT", "Montana"], ["NE", "Nebraska"], ["NV", "Nevada"],
  ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"], ["NY", "New York"],
  ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"], ["OK", "Oklahoma"], ["OR", "Oregon"],
  ["PA", "Pennsylvania"], ["RI", "Rhode Island"], ["SC", "South Carolina"], ["SD", "South Dakota"],
  ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"], ["VT", "Vermont"], ["VA", "Virginia"],
  ["WA", "Washington"], ["WV", "West Virginia"], ["WI", "Wisconsin"], ["WY", "Wyoming"],
];

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

export default function CheckoutPage() {
  const { items, subtotal, savings, net, discountCents, clear } = useCart();
  const { user } = useAuth();
  const { testMode } = useSettings();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    address1: "", address2: "", city: "", region: "", zip: "", country: "US",
  });
  const [ship, setShip] = useState({ flat: 699, threshold: 0 });
  const [shipOptions, setShipOptions] = useState([]);
  const [shipId, setShipId] = useState("standard");
  const [rewards, setRewards] = useState({ points: 0, redeemableCents: 0 });
  const [applyPoints, setApplyPoints] = useState(false);
  const [taxRates, setTaxRates] = useState({});

  useEffect(() => {
    api.get("/config").then((r) => {
      setShip({
        flat: r.data.shipping_flat_cents,
        threshold: r.data.free_shipping_threshold_cents,
      });
      setTaxRates(r.data.tax_rates || {});
      const opts = r.data.shipping_options || [];
      setShipOptions(opts);
      if (opts.length && !opts.some((o) => o.id === shipId)) setShipId(opts[0].id);
    }).catch(() => {});
  }, []);

  // Points are tied to the logged-in ACCOUNT (guests can check out but earn/redeem nothing).
  useEffect(() => {
    if (!user) {
      setRewards({ points: 0, redeemableCents: 0 });
      setApplyPoints(false);
      return;
    }
    // Prefill the email with the account email.
    setForm((f) => (f.email ? f : { ...f, email: user.email }));
    api.get("/account/rewards")
      .then((r) => {
        const per = r.data.points_per_dollar_redeem || 200;
        const redeemableCents = Math.floor((r.data.points || 0) / per) * 100;
        setRewards({ points: r.data.points || 0, redeemableCents });
      })
      .catch(() => setRewards({ points: 0, redeemableCents: 0 }));
  }, [user]);

  const selectedShip = shipOptions.find((o) => o.id === shipId);
  const freeShipEligible = ship.threshold > 0 && subtotal >= ship.threshold;
  const shipPriceFor = (o) => (freeShipEligible && o.printify_method === 1 ? 0 : o.price_cents);
  const shippingCost = selectedShip ? shipPriceFor(selectedShip) : ship.flat;
  // Rewards discount: capped to merchandise subtotal, only when the shopper opts in.
  const rewardDiscount = applyPoints ? Math.min(rewards.redeemableCents, subtotal) : 0;
  const redeemPoints = (rewardDiscount / 100) * 200;
  // Sales tax on net merchandise (not shipping), only where we collect it.
  const taxRate = taxRates[(form.region || "").toUpperCase()] || 0;
  const taxAmount = Math.round(Math.max(subtotal - rewardDiscount, 0) * taxRate);
  const grandTotal = subtotal + shippingCost - rewardDiscount + taxAmount;
  const remaining = ship.threshold > 0 ? Math.max(0, ship.threshold - subtotal) : 0;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const required = ["first_name", "last_name", "email", "address1", "city", "region", "zip"];
  const formValid = required.every((k) => (form[k] || "").trim().length > 0);

  const buildPayload = () => ({
    line_items: items.map((i) => ({
      product_id: i.product_id,
      variant_id: i.variant_id,
      quantity: i.quantity,
      custom_name: i.custom_name,
      custom_number: i.custom_number,
      custom_color: i.custom_color,
    })),
    shipping_address: form,
    redeem_points: applyPoints ? redeemPoints : 0,
    shipping_method_id: shipId,
  });

  const createOrder = async () => {
    if (!formValid) {
      toast.error("Please fill in your shipping details first.");
      throw new Error("form invalid");
    }
    const r = await api.post("/paypal/create-order", buildPayload());
    return r.data.paypal_order_id;
  };

  const onApprove = async (data) => {
    try {
      const r = await api.post("/paypal/capture-order", { paypal_order_id: data.orderID });
      if (r.data.status === "paid" || r.data.status === "already_paid") {
        clear();
        navigate(`/order-success?order_id=${r.data.order?.id || ""}`);
      } else {
        toast.error("Payment could not be completed.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Payment failed. Please try again.");
    }
  };

  const [simulating, setSimulating] = useState(false);
  const runTestCheckout = async () => {
    if (!formValid) {
      toast.error("Please fill in your shipping details first.");
      return;
    }
    setSimulating(true);
    try {
      const r = await api.post("/checkout/test", buildPayload());
      if (r.data.status === "test_paid") {
        toast.success("Test order placed — no charge, no production.");
        clear();
        navigate(`/order-success?order_id=${r.data.order?.id || ""}`);
      } else {
        toast.error("Test checkout could not be completed.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Test checkout failed. Please try again.");
    } finally {
      setSimulating(false);
    }
  };

  if (items.length === 0) {
    return (
      <div data-testid="checkout-empty" className="text-center py-40 bg-background min-h-screen">
        <p className="font-anton text-3xl uppercase text-white">Your cart is empty</p>
        <Link to="/#shop" className="text-primary underline mt-4 inline-block font-bold uppercase tracking-widest text-sm">
          Shop now
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="grid lg:grid-cols-2 bg-background">
      {/* Form */}
      <div className="px-6 md:px-12 lg:px-16 py-16">
        <h1 className="font-anton text-5xl uppercase tracking-tight mb-2 text-white">Checkout</h1>
        <p className="text-white/50 mb-10 flex items-center gap-2 text-sm"><Lock className="w-4 h-4" /> Shipping details</p>
        <div className="grid grid-cols-2 gap-4">
          {FIELDS.map(([key, label, type]) => (
            <div key={key} className={["address1", "address2", "email"].includes(key) ? "col-span-2" : "col-span-2 sm:col-span-1"}>
              <label className="text-xs uppercase tracking-widest font-bold text-white/50">{label}</label>
              {type === "select" ? (
                <select
                  data-testid={`checkout-${key}`}
                  value={form[key]}
                  onChange={set(key)}
                  className="mt-2 bg-background border border-white/20 focus:border-primary outline-none p-3.5 w-full text-white"
                >
                  <option value="">Select a state</option>
                  {US_STATES.map(([code, name]) => (
                    <option key={code} value={code}>{name}</option>
                  ))}
                </select>
              ) : (
                <input
                  data-testid={`checkout-${key}`}
                  type={type}
                  value={form[key]}
                  onChange={set(key)}
                  className="mt-2 bg-transparent border border-white/20 focus:border-primary outline-none p-3.5 w-full text-white"
                />
              )}
            </div>
          ))}
        </div>

        {/* Shipping speed */}
        {shipOptions.length > 0 && (
          <div className="mt-8" data-testid="shipping-options">
            <p className="text-xs uppercase tracking-widest font-bold text-white/50 mb-3 flex items-center gap-2">
              <Truck className="w-4 h-4" /> Delivery speed
            </p>
            <div className="space-y-3">
              {shipOptions.map((o) => {
                const active = o.id === shipId;
                const fast = o.id !== "standard";
                const isFree = shipPriceFor(o) === 0;
                return (
                  <button
                    key={o.id}
                    type="button"
                    data-testid={`ship-option-${o.id}`}
                    onClick={() => setShipId(o.id)}
                    className={`w-full flex items-center justify-between p-4 border text-left transition-colors ${
                      active ? "border-primary bg-primary/5" : "border-white/20 hover:border-white/40"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${active ? "border-primary bg-primary" : "border-white/40"}`} />
                      <div>
                        <span className="font-bold uppercase text-sm text-white flex items-center gap-1.5">
                          {fast && <Zap className="w-3.5 h-3.5 text-primary" />}{o.label}
                        </span>
                        <span className="block text-xs text-white/50 mt-0.5">Arrives in {o.estimate}</span>
                      </div>
                    </div>
                    <span className="font-bold text-sm text-white">
                      {isFree ? (
                        <>
                          <span className="text-white/40 line-through mr-2 font-normal">{formatPrice(o.price_cents)}</span>
                          <span className="text-primary uppercase">Free</span>
                        </>
                      ) : formatPrice(o.price_cents)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Rewards redemption */}
        {!user && (
          <div className="mt-8 border border-white/15 bg-white/5 p-5" data-testid="rewards-login-prompt">
            <p className="flex items-center gap-2 font-bold text-white uppercase tracking-widest text-sm">
              <Gift className="w-4 h-4 text-primary" /> Earn points on this order
            </p>
            <p className="text-xs text-white/60 mt-1.5">
              <Link to="/login" state={{ from: "/checkout" }} className="text-primary hover:text-white font-bold underline underline-offset-2 inline-flex items-center gap-1">
                <LogIn className="w-3.5 h-3.5" /> Log in or create an account
              </Link>{" "}
              to earn Blend Rewards points and track your orders. You can still check out as a guest.
            </p>
          </div>
        )}
        {user && rewards.points > 0 && (
          <div className="mt-8 border border-primary/30 bg-primary/5 p-5" data-testid="rewards-redeem">
            <div className="flex items-start gap-3">
              <input
                id="apply-points"
                type="checkbox"
                data-testid="apply-points-checkbox"
                checked={applyPoints}
                onChange={(e) => setApplyPoints(e.target.checked)}
                disabled={rewards.redeemableCents === 0}
                className="mt-1 w-4 h-4 accent-primary"
              />
              <label htmlFor="apply-points" className="flex-1 cursor-pointer">
                <span className="flex items-center gap-2 font-bold text-white uppercase tracking-widest text-sm">
                  <Gift className="w-4 h-4 text-primary" /> Use your rewards
                </span>
                <span data-testid="rewards-balance" className="block text-xs text-white/60 mt-1">
                  You have <b className="text-primary">{rewards.points.toLocaleString()} points</b>
                  {rewards.redeemableCents > 0
                    ? <> — worth <b className="text-primary">{formatPrice(rewards.redeemableCents)}</b> off</>
                    : <> — earn a bit more to redeem (200 points = $1)</>}
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Payment */}
        <div className="mt-8" data-testid="paypal-section">
          {!formValid && (
            <p className="text-xs text-white/50 mb-3 uppercase tracking-widest font-bold">
              Fill in your details to unlock payment
            </p>
          )}
          {testMode ? (
            <div data-testid="test-mode-checkout">
              <div className="flex items-start gap-3 border border-primary/40 bg-primary/10 p-4 mb-4" data-testid="test-mode-banner">
                <FlaskConical className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold uppercase tracking-widest text-sm text-primary">Test Mode is on</p>
                  <p className="text-xs text-white/60 mt-1">
                    This is a safe simulation — no card is charged and no order is sent to production.
                  </p>
                </div>
              </div>
              <button
                type="button"
                data-testid="test-checkout-button"
                onClick={runTestCheckout}
                disabled={!formValid || simulating}
                className="w-full bg-primary text-black font-bold uppercase tracking-widest text-sm py-4 hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <FlaskConical className="w-4 h-4" />
                {simulating ? "Placing test order…" : "Place test order (no charge)"}
              </button>
            </div>
          ) : PAYPAL_CLIENT_ID ? (
            <div className={formValid ? "" : "opacity-50 pointer-events-none"}>
              <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "USD", components: "buttons" }}>
                <PayPalButtons
                  style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
                  disabled={!formValid}
                  forceReRender={[grandTotal, formValid, rewardDiscount, shippingCost]}
                  createOrder={createOrder}
                  onApprove={onApprove}
                  onError={() => toast.error("Payment error. Please try again.")}
                />
              </PayPalScriptProvider>
            </div>
          ) : (
            <p className="text-sm text-red-400">Payments are not configured.</p>
          )}
          {!testMode && (
            <p className="text-xs text-white/40 mt-3">Pay with PayPal, Pay Later, or a debit/credit card — no PayPal account required.</p>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="bg-card px-6 md:px-12 lg:px-16 py-16 border-l border-white/10">
        <h2 className="font-anton text-2xl uppercase tracking-tight mb-8 text-white">Order Summary</h2>
        <div className="divide-y divide-white/10">
          {items.map((it) => (
            <div key={it.key} className="flex gap-4 py-4">
              <div className="w-16 h-20 bg-neutral-900 overflow-hidden flex-shrink-0">
                {it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1">
                <p className="font-bold uppercase text-sm text-white">{it.title}</p>
                <p className="text-xs text-white/50">Size {it.size}{it.custom_color ? ` · ${it.custom_color}` : ""} · Qty {it.quantity}</p>
                {(it.custom_name || it.custom_number) && (
                  <p className="text-xs text-primary font-bold mt-0.5 uppercase">
                    {it.custom_name}{it.custom_number ? ` #${it.custom_number}` : ""}
                  </p>
                )}
              </div>
              <span className="font-bold text-sm text-white">
                {discountCents > 0 && (
                  <span className="text-white/40 line-through mr-2 font-normal">{formatPrice(it.price * it.quantity)}</span>
                )}
                {formatPrice(net(it.price) * it.quantity)}
              </span>
            </div>
          ))}
        </div>
        <div className="border-t border-white/20 mt-6 pt-6 space-y-2">
          {savings > 0 && (
            <div className="flex justify-between text-sm text-primary font-bold uppercase tracking-widest">
              <span>Discount</span><span data-testid="summary-savings">−{formatPrice(savings)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm text-white/60"><span>Subtotal</span><span data-testid="summary-subtotal">{formatPrice(subtotal)}</span></div>
          {rewardDiscount > 0 && (
            <div className="flex justify-between text-sm text-primary font-bold uppercase tracking-widest">
              <span>Rewards ({redeemPoints.toLocaleString()} pts)</span><span data-testid="summary-rewards">−{formatPrice(rewardDiscount)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm text-white/60">
            <span>Shipping{selectedShip ? ` · ${selectedShip.label}` : ""}</span>
            <span data-testid="summary-shipping">{shippingCost === 0 ? <span className="text-primary font-bold uppercase">Free</span> : formatPrice(shippingCost)}</span>
          </div>
          {selectedShip && (
            <p data-testid="summary-ship-estimate" className="text-xs text-white/40">Arrives in {selectedShip.estimate}</p>
          )}
          {remaining > 0 && (
            <p data-testid="free-shipping-hint" className="text-xs text-primary font-bold">
              Add {formatPrice(remaining)} more for FREE Standard shipping
            </p>
          )}
          {freeShipEligible && (
            <p data-testid="free-shipping-unlocked" className="text-xs text-primary font-bold uppercase tracking-widest">
              ✓ You've unlocked free Standard shipping
            </p>
          )}
          {taxAmount > 0 && (
            <div className="flex justify-between text-sm text-white/60">
              <span>Sales tax ({(taxRate * 100).toFixed(2).replace(/\.?0+$/, "")}%)</span>
              <span data-testid="summary-tax">{formatPrice(taxAmount)}</span>
            </div>
          )}
          <div className="flex justify-between font-anton text-2xl pt-2 text-white"><span>Total</span><span data-testid="summary-total">{formatPrice(grandTotal)}</span></div>
          <p data-testid="points-earn-hint" className="flex items-center gap-1.5 text-xs text-primary font-bold pt-1">
            <Gift className="w-3.5 h-3.5" /> You'll earn {Math.floor(subtotal / 10).toLocaleString()} points on this order
          </p>
        </div>
      </div>
    </div>
  );
}
